import { useEffect, useState } from "react";
import { motion, useScroll } from "framer-motion";
import { Menu, Rocket, Globe, ChevronDown, Check } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogoLockup } from "@/components/brand/Logo";
import { SocialLinks } from "@/components/SocialLinks";
import { NAV, LINKS } from "@/config/kingbullish";
import { scrollToId } from "@/lib/lenis";
import { useLocation, useNavigate } from "react-router-dom";

const NavLink = ({ item, active, onNavigate, className = "", placement = "nav" }) => {
  if (item.external) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" className={`nav-link ${className}`} data-testid={`${placement}-link-${item.id}`}>
        {item.label}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onNavigate(item.target)}
      aria-current={active ? "location" : undefined}
      className={`nav-link ${active ? "is-active" : ""} ${className}`}
      data-testid={`${placement}-link-${item.id}`}
    >
      {item.label}
    </button>
  );
};

const LanguageSelector = ({ placement = "header" }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        className="inline-flex items-center gap-1.5 rounded-md border border-white/12 bg-white/[0.03] px-2.5 py-2 font-display text-[0.7rem] font-semibold tracking-widest text-white/85 transition-colors hover:border-neon/60 hover:text-white"
        data-testid={`${placement}-language-selector`}
      >
        <Globe className="h-4 w-4 text-neon" />
        EN
        <ChevronDown className="h-3.5 w-3.5 opacity-70" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="min-w-[180px] border-white/10 bg-satin/95 backdrop-blur-xl font-body">
      <DropdownMenuItem className="justify-between font-semibold tracking-wider text-white focus:bg-neon/10 focus:text-neon" data-testid={`${placement}-language-option-en`}>
        ENGLISH (EN) <Check className="h-4 w-4 text-neon" />
      </DropdownMenuItem>
      <DropdownMenuItem disabled className="text-xs tracking-wider text-white/40">
        MORE LANGUAGES COMING SOON
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

export const Header = () => {
  const { pathname } = useLocation();
  const routeNavigate = useNavigate();
  const isBuy = /^\/buy\/?$/.test(pathname);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    setActive(isBuy ? null : "home");
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname, isBuy]);

  useEffect(() => {
    const ids = NAV.filter((n) => n.target).map((n) => n.target);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-40% 0px -55% 0px" },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [pathname]);

  const navigate = (target) => {
    setOpen(false);
    if (pathname !== "/") routeNavigate(target === "home" ? "/" : `/#${target}`);
    else scrollToId(target);
  };

  return (
    <motion.header
      initial={{ y: -90, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={`header-glass fixed inset-x-0 top-0 z-50 ${scrolled ? "header-glass--scrolled" : ""}`}
      data-testid="site-header"
    >
      {isBuy && <span className="buy-page-version" data-testid="buy-page-version" aria-label="Version 1">V1</span>}
      <div className="container-x flex h-[72px] items-center justify-between gap-2 sm:gap-4">
        <button type="button" onClick={() => navigate("home")} className="shrink-0 text-left" data-testid="header-logo">
          <LogoLockup size={44} priority testId="header-lockup" />
        </button>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink key={item.id} item={item} active={active === item.target} onNavigate={navigate} />
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <SocialLinks placement="header" compact className="header-socials" />
          <span className="hidden sm:block">
            <a href={LINKS.buy} aria-current={isBuy ? "page" : undefined} className="btn-neon !py-2.5 !px-3 !text-[0.72rem] xl:!px-4 xl:!text-[0.82rem]" data-testid="header-buy-kbull-button">
              BUY $KBULL <Rocket className="h-4 w-4" />
            </a>
          </span>
          <span className="hidden min-[390px]:block"><LanguageSelector /></span>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-md border border-white/12 bg-white/[0.03] text-white lg:hidden"
                aria-label="Open menu"
                data-testid="mobile-menu-button"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" aria-describedby={undefined} className="w-[300px] border-l border-neon/25 bg-ink/95 backdrop-blur-2xl text-white" data-testid="mobile-menu">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="mt-6 flex flex-col gap-2">
                <LogoLockup size={40} className="mb-6" testId="mobile-menu-lockup" />
                {NAV.map((item) => (
                  <NavLink key={item.id} item={item} placement="mobile-nav" active={active === item.target} onNavigate={navigate} className="!text-sm !py-3 text-left" />
                ))}
                <a href={LINKS.buy} className="btn-neon mt-6" data-testid="mobile-buy-kbull-button">
                  BUY $KBULL <Rocket className="h-4 w-4" />
                </a>
                <div className="mt-5 border-t border-gold/20 pt-5" data-testid="mobile-community-area">
                  <p className="mb-3 font-body text-xs font-semibold text-neon" data-testid="mobile-community-heading">
                    JOIN THE KINGBULLISH COMMUNITY
                  </p>
                  <SocialLinks placement="mobile" compact />
                  <div className="mt-5 min-[390px]:hidden">
                    <LanguageSelector placement="mobile" />
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <motion.div className="scroll-progress" style={{ scaleX: scrollYProgress }} aria-hidden="true" data-testid="scroll-progress" />
    </motion.header>
  );
};
